import React, { useMemo, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { CalendarCheck, ShieldCheck, Info } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import { findBookable } from "../data/consultants";
import { ConsultantPicker } from "../components/booking/ConsultantPicker";
import { SlotPicker } from "../components/booking/SlotPicker";
import { BookingForm } from "../components/booking/BookingForm";
import { ReasonPicker, REASONS } from "../components/booking/ReasonPicker";
import { HolterDays } from "../components/booking/HolterDays";
import { OfflineConsultant } from "../components/booking/OfflineConsultant";
import { OFFLINE_SLUGS } from "../data/consultants";
import { planVisit, TESTS } from "../data/fees";
import { SERVICES } from "../data/services";

const REASON_SLUGS = SERVICES.map((s) => s.slug);
const NOT_STANDALONE = REASONS.map((r) => r.slug).filter((s) => !TESTS[s]?.standalone);

export default function BookAppointmentPayFirst() {
  const [params, setParams] = useSearchParams();
  const consultant = useMemo(() => findBookable(params.get("consultant") || ""), [params]);
  const [pick, setPick] = useState(consultant ? consultant.slug : params.get("consultant") === "tests" ? "tests" : "");
  const consultantSlug = pick === "any" || pick === "tests" ? "" : pick;
  const choosePick = (slug) => {
    setPick(slug); setSlot(null);
    if (slug === "tests") setReasons((r) => r.filter((x) => !NOT_STANDALONE.includes(x)));
    setParams(slug === "any" ? {} : { consultant: slug }, { replace: true });
  };
  const [slot, setSlot] = useState(null);
  const [reasons, setReasons] = useState(() => (params.get("reasons") || "").split(",").filter((r) => REASON_SLUGS.includes(r)));
  const [refreshKey, setRefreshKey] = useState(0);
  const formRef = useRef(null);
  const pickSlot = (iso) => {
    setSlot(iso);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const slotTaken = () => { setSlot(null); setRefreshKey((k) => k + 1); };
  const [holterDays, setHolterDays] = useState(1);
  const testsPick = pick === "tests";
  const offline = OFFLINE_SLUGS.includes(pick) ? findBookable(pick) : null;
  const plan = useMemo(() => planVisit(reasons, "initial", holterDays, testsPick), [reasons, holterDays, testsPick]);
  const ready = reasons.length > 0 && !!pick && !offline && (!testsPick || plan.testsOnly);
  const changeReasons = (r) => { setReasons(r); setSlot(null); };
  const slotConsultant = plan.testsOnly ? "any" : pick;

  if (params.get("rebook")) return <Navigate to="/follow-up" replace />;

  return (
    <div data-testid="book-appointment-page">
      <SEO
        title="Book an Appointment"
        description="Book a consultation with Cambridge Cardiovascular Prevention Clinic. Choose a time that suits you, then confirm your booking with secure payment."
        path="/enquiry"
      />

      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-16 max-w-5xl">
          <div className="eyebrow">Book an appointment</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            Choose a time. <em className="font-serif text-bronze-dark">Pay to confirm.</em>
          </h1>
          <p className="mt-6 text-ink-muted text-lg max-w-3xl leading-relaxed">
            Pick a time, add your details and pay securely. <b>Nothing is booked until payment succeeds</b> - then your confirmation email and calendar invite follow straight away.
          </p>
        </div>
      </section>

      <section className="container-ccc py-12 max-w-6xl">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-6">
            <ConsultantPicker value={pick} onChange={choosePick} step={1} testsOption />
            {offline ? <OfflineConsultant consultant={offline} /> : pick && <>
              <ReasonPicker value={reasons} onChange={changeReasons} step={2} testsOnly={testsPick} exclude={testsPick ? NOT_STANDALONE : []} />
              <HolterDays reasons={reasons} value={holterDays} onChange={setHolterDays} />
              {plan.testsOnly && <p data-testid="booking-tests-only-note" className="text-sm p-4 bg-cream-100 border border-bronze-light/40 rounded-md">You&apos;re booking tests only, without a consultation. Your results are reviewed and reported by the clinic.</p>}
            </>}
            {offline ? null : ready ? (
              <SlotPicker value={slot} onChange={pickSlot} refreshKey={refreshKey} holterDays={holterDays} consultant={slotConsultant}
                tests={plan.tests} visit={plan.lookupKey} step={3} />
            ) : (
              <p data-testid="booking-steps-hint" className="text-sm text-ink-muted">
                {!pick ? "Choose a consultant above - or \u201cTests only\u201d - to get started." : "Tick at least one reason above to see available times."}
              </p>
            )}
            <div ref={formRef} className="scroll-mt-28">
              {slot && ready ? (
                <BookingForm slot={slot} consultantSlug={plan.testsOnly ? "" : consultantSlug} reasons={reasons} plan={plan} onSlotTaken={slotTaken} />
              ) : !offline && (
                <p data-testid="booking-pick-hint" className="text-xs text-ink-muted flex items-start gap-2">
                  <Info size={14} weight="duotone" className="text-bronze-dark mt-0.5 shrink-0" />
                  Times shown in UK time. Choose a time to continue - nothing is booked until you&apos;ve paid.
                </p>
              )}
            </div>
          </div>

          <aside className="lg:col-span-4 space-y-5">
            <div className="p-6 bg-cream-100 border border-bronze-light/40 rounded-md">
              <div className="eyebrow flex items-center gap-2 text-bronze-dark">
                <CalendarCheck size={16} weight="duotone" /> How it works
              </div>
              <ol className="mt-4 space-y-3 text-sm text-ink-muted list-decimal list-inside">
                <li>Choose your consultant (or first available, or tests only).</li>
                <li>Tick what you&apos;d like to see us about.</li>
                <li>Pick a date and time - from 7 days ahead.</li>
                <li>Add your name, email, phone and NHS number (if you have one).</li>
                <li>Pay securely - the full price, including any tests you tick, is shown on the secure payment page before you pay. Tests are done just before your consultation in the same visit, by our nurse or echo physiologist - your consultant sees you for the consultation. Echo and ABPM can also be booked on their own. Already seen by us? Follow-ups are booked from your patient account.</li>
                <li>We see self-pay patients only - we don&apos;t accept private medical insurance.</li>
                <li>Your appointment is booked and we email your confirmation + calendar invite.</li>
              </ol>
            </div>

            <div className="p-6 bg-white border border-stone-200/60 rounded-md">
              <div className="eyebrow flex items-center gap-2 text-bronze-dark">
                <ShieldCheck size={16} weight="duotone" /> Payment &amp; refunds
              </div>
              <ul className="mt-4 space-y-2 text-sm text-ink-muted">
                <li>Card payments processed by Stripe with 3-D Secure.</li>
                <li>Full refund if you cancel 48+ hours ahead.</li>
                <li>50% refund between 24-48 hours before.</li>
                <li>No refund inside 24 hours.</li>
              </ul>
            </div>

          </aside>
        </div>
      </section>
    </div>
  );
}
